import styles from 'css/src/components/common/ui/TimeAgo.module.scss';

import React from 'react';
import classNames from 'classnames';

import { formatDistanceToNow, isAfter, parseISO } from 'date-fns';

type Props = {
    time: string;
    className?: string;
    muted?: boolean;
};

/**
 * Uses date-fns to render a readable time component where it returns:
 * 0-1 minute: Just now
 * 1-59 minutes: {time} minutes
 * 1-23 hours: {time} hours
 * 1-x days: {time} days
 *
 */
const TimeAgo = React.memo(function TimeAgo({ time, className = '', muted = false }: Props) {
    const date = parseISO(time);
    // Handle case where time is in the future
    const now = new Date();
    const isFuture = isAfter(date, now);

    // If date is in the future, show distance from now, otherwise from the past
    let timeAgo = formatDistanceToNow(isFuture ? now : date, {
        addSuffix: true,
    });

    // Replace "less than a minute ago" with "Just now"
    if (timeAgo.includes('less than a minute ago')) {
        timeAgo = 'Just now';
    }

    return (
        // The browser hydrating a server render has a later "now" than the server did.
        <span
            className={classNames(className, styles['time-ago'], { [styles['muted']]: muted })}
            suppressHydrationWarning
        >
            {timeAgo}
        </span>
    );
});

export default TimeAgo;
